/**
 * Step 2 — Connect WhatsApp (plan whatsapp-byo-flow.md, decision #4: equal fork).
 * Two ways: connect your OWN number (QR pairing → pick chats) or use Brain's
 * shared number (paste a group invite link). Picking either routes to its flow.
 */
import { Phone, QrCode } from 'lucide-react';
import { useState } from 'react';
import { IconTile } from '@/components/patterns/icon-tile';
import { StepLink } from './step-link';
import { WahaChatPicker } from './waha-chat-picker';
import { WahaQrPair } from './waha-qr-pair';

interface Props {
  onNext: () => void;
}

type Mode = 'fork' | 'byo' | 'shared';

export function StepConnect({ onNext }: Props) {
  const [mode, setMode] = useState<Mode>('fork');
  const [byoSessionId, setByoSessionId] = useState<string | null>(null);

  if (mode === 'byo') {
    return byoSessionId ? (
      <WahaChatPicker sessionId={byoSessionId} onDone={onNext} />
    ) : (
      <WahaQrPair onConnected={(sid) => setByoSessionId(sid)} onBack={() => setMode('fork')} />
    );
  }

  if (mode === 'shared') {
    return (
      <div className="space-y-4">
        <button
          type="button"
          onClick={() => setMode('fork')}
          className="text-[13px] text-faint transition-colors hover:text-foreground"
        >
          ← Choose a different way to connect
        </button>
        <StepLink onNext={onNext} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-[14px] text-muted-foreground">
        Two ways to connect WhatsApp — pick whichever fits.
      </p>

      <button
        type="button"
        onClick={() => setMode('byo')}
        className="block w-full rounded-xl border border-border p-6 text-left transition-colors hover:border-foreground/30"
      >
        <div className="flex items-start gap-4">
          <IconTile tone="green">
            <QrCode className="h-5 w-5" />
          </IconTile>
          <div>
            <h3 className="text-[15px] font-semibold">Connect your own number</h3>
            <p className="mt-1 text-[13.5px] text-muted-foreground">
              Scan a QR code to link your WhatsApp, then choose any chats — groups or DMs — to
              index, with recent history.
            </p>
          </div>
        </div>
      </button>

      <button
        type="button"
        onClick={() => setMode('shared')}
        className="block w-full rounded-xl border border-border p-6 text-left transition-colors hover:border-foreground/30"
      >
        <div className="flex items-start gap-4">
          <IconTile tone="neutral">
            <Phone className="h-5 w-5" />
          </IconTile>
          <div>
            <h3 className="text-[15px] font-semibold">Use Brain&apos;s number</h3>
            <p className="mt-1 text-[13.5px] text-muted-foreground">
              Paste a group&apos;s invite link and Brain&apos;s number joins it. Best for a single
              shared group.
            </p>
          </div>
        </div>
      </button>
    </div>
  );
}
